/**
 * User Service
 *
 * Manages user profiles and settings initialization in Firestore.
 *
 * ## Data Model: Onboarding vs Settings
 *
 * Each user has a top-level document at `users/{userId}` containing their
 * profile (name, email, plan, timestamps). Two subcollection families sit
 * beneath the user document:
 *
 * ### `settings` subcollection (`users/{userId}/settings/{category}`)
 * Seven category documents (profile, investableAssets, realEstateAssets,
 * liabilities, income, expenses, assumptions). Created by
 * `initializeUserSettings()` when the user first signs up — every field is
 * seeded as `null`. The `SettingsContext` subscribes to these documents and
 * constructs typed Settings classes that provide defaults for null fields:
 * - Financial fields (assets, income, etc.) → default to 0
 * - Assumption fields (return rate, inflation, etc.) → fall back to
 *   age-appropriate statistics from static JSON files
 *
 * ### `onboarding` subcollection (`users/{userId}/onboarding/status`)
 * A single `status` document tracking onboarding progress:
 * - `welcomeViewed` / `welcomeViewedAt` — set by `markWelcomeViewed()`
 *   when the user dismisses the welcome screen
 * - `completed` / `completedAt` — set by `completeOnboarding()` when the
 *   user finishes all onboarding steps (intake → quick-peek → data-entry
 *   → celebration)
 *
 * Onboarding is considered **complete** when `completed === true`. Until
 * then, the `OnboardingGuard` component redirects authenticated users
 * to the appropriate onboarding step. After completion, users land on the
 * dashboard and can edit settings freely via the settings pages.
 *
 * ### Lifecycle Flow
 * 1. User signs up → `createUserProfile()` writes user doc + calls
 *    `initializeUserSettings()` (all null seeds)
 * 2. User views welcome → `markWelcomeViewed()` writes onboarding status
 * 3. User completes onboarding steps (form saves write to settings docs)
 * 4. User finishes celebration → `completeOnboarding()` marks done
 * 5. Post-onboarding: settings updates flow through `settingsService`
 */

import { getFirestoreClient } from "../lib/firestoreClient";
import { Plan, UserProfile } from "../types/user";
import type { AuthProvider } from "../types/user";
import type { Result } from "../types/result";
import { Ok, Err } from "../types/result";
import type { AppError } from "../types/errors";
import { createUnexpectedError, mapFirebaseError } from "../types/errors";
import { createLogger } from "../utils/logger";
import type { SettingsCategory } from "../types/settings";

const userServiceLogger = createLogger({ prefix: "UserService" });

const nullSeedByCategory: Record<SettingsCategory, Record<string, null>> = {
  profile: { yearOfBirth: null, isRetired: null },
  liabilities: {
    mortgage: null,
    creditCardBalance: null,
    carLoan: null,
    lineOfCredit: null,
    studentLoans: null,
    otherLiabilities: null,
    mortgageInterestRate: null,
    creditCardInterestRate: null,
    carLoanInterestRate: null,
    lineOfCreditInterestRate: null,
    studentLoanInterestRate: null,
    otherLiabilitiesInterestRate: null,
  },
  income: {
    employmentIncome: null,
    pensionIncome: null,
    cppIncome: null,
    oasIncome: null,
    investmentIncome: null,
    rentalIncome: null,
    otherIncome: null,
  },
  expenses: {
    housing: null,
    utilities: null,
    food: null,
    transportation: null,
    insurance: null,
    healthcare: null,
    other: null,
    mortgagePayment: null,
    creditCardPayment: null,
    carLoanPayment: null,
    lineOfCreditPayment: null,
    studentLoanPayment: null,
    otherLiabilitiesPayment: null,
  },
  assumptions: {
    retirementAge: null,
    lifeExpectancy: null,
    expectedReturnRate: null,
    inflationRate: null,
    expensesAtRetirement: null,
  },
  investableAssets: {
    cashSavings: null,
    emergencyFund: null,
    gicTermDeposits: null,
    rrspBalance: null,
    spousalRrspBalance: null,
    tfsaBalance: null,
    fhsaBalance: null,
    respBalance: null,
    liraBalance: null,
    rrifBalance: null,
    dpspBalance: null,
    nonRegisteredInvestments: null,
    dbPensionCommutedValue: null,
    dcPensionBalance: null,
    groupRrspBalance: null,
    stockOptionsRsusValue: null,
  },
  realEstateAssets: {
    primaryResidence: null,
    vacationProperty: null,
    investmentProperties: null,
    landOtherRealEstate: null,
    businessValue: null,
    shareholderLoansReceivable: null,
    insuranceCashValue: null,
    otherAssets: null,
    depreciatingAssets: null,
  },
};

/**
 * Parameters for creating or updating a user profile
 */
export interface CreateUserProfileParams {
  userId: string;
  email: string | null;
  firstName: string;
  lastName: string;
  provider: AuthProvider;
  providerUserId: string;
  avatarUrl?: string | null;
}

/**
 * Create or update user profile in Firestore.
 * Writes are enqueued — the queue auto-flushes on timer / page-hide.
 * @returns Result containing the created/updated UserProfile object
 */
export async function createUserProfile(
  params: CreateUserProfileParams
): Promise<Result<UserProfile, AppError>> {
  const {
    userId,
    email,
    firstName,
    lastName,
    provider,
    providerUserId,
    avatarUrl,
  } = params;

  userServiceLogger.debug("Creating/updating user profile", {
    userId,
    provider,
  });

  const client = getFirestoreClient();
  const userPath = ["users", userId];

  try {
    const userDoc = await client.getDocument(userPath);

    if (userDoc.exists()) {
      userServiceLogger.debug("User exists, updating profile", { userId });
      const existingData = userDoc.data()!;
      const updates: Record<string, unknown> = {
        lastSignInAt: client.serverTimestamp(),
      };

      if (existingData.email !== email) updates.email = email;
      if (existingData.firstName !== firstName) updates.firstName = firstName;
      if (existingData.lastName !== lastName) updates.lastName = lastName;
      if (avatarUrl && existingData.avatarUrl !== avatarUrl)
        updates.avatarUrl = avatarUrl;

      await client.setDocument(userPath, updates, { merge: true });
      userServiceLogger.info("User profile updated", { userId });

      return Ok(
        new UserProfile({
          userId,
          email: email || "",
          firstName,
          lastName,
          avatarUrl: avatarUrl || undefined,
          provider,
          providerUserId,
          plan: existingData.plan as ReturnType<typeof Plan.prototype.toJSON>,
          createdAt: (existingData.createdAt as { toDate(): Date }).toDate(),
          lastSignInAt: new Date(),
        })
      );
    } else {
      userServiceLogger.debug("Creating new user profile", { userId });

      await client.setDocument(
        userPath,
        {
          email,
          firstName,
          lastName,
          avatarUrl: avatarUrl || null,
          provider,
          providerUserId,
          plan: Plan.fromName("basic").toJSON(),
          createdAt: client.serverTimestamp(),
          lastSignInAt: client.serverTimestamp(),
        } as Record<string, unknown>,
        { merge: true }
      );

      const settingsInitResult = await initializeUserSettings(userId);
      if (!settingsInitResult.ok) {
        return Err(settingsInitResult.error);
      }

      userServiceLogger.info("New user profile created", { userId });

      return Ok(
        new UserProfile({
          userId,
          email: email || "",
          firstName,
          lastName,
          avatarUrl: avatarUrl || undefined,
          provider,
          providerUserId,
          plan: Plan.fromName("basic").toJSON(),
          createdAt: new Date(),
          lastSignInAt: new Date(),
        })
      );
    }
  } catch (error) {
    userServiceLogger.error("Firebase error in createUserProfile", {
      error,
      userId,
      provider,
    });
    return Err(mapFirebaseError(error));
  }
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(params: {
  userId: string;
}): Promise<Result<UserProfile | null, AppError>> {
  const { userId } = params;

  userServiceLogger.debug("Fetching user profile", { userId });

  const client = getFirestoreClient();
  const userPath = ["users", userId];

  try {
    const userDoc = await client.getDocument(userPath);

    if (!userDoc.exists()) {
      userServiceLogger.debug("User profile not found", { userId });
      return Ok(null);
    }

    const data = userDoc.data()!;

    return Ok(
      new UserProfile({
        userId,
        email: data.email as string,
        firstName: data.firstName as string,
        lastName: data.lastName as string,
        avatarUrl: (data.avatarUrl as string) || undefined,
        provider: data.provider as AuthProvider,
        providerUserId: data.providerUserId as string,
        plan: data.plan as ReturnType<typeof Plan.prototype.toJSON>,
        privacyAcceptedAt: data.privacyAcceptedAt
          ? (data.privacyAcceptedAt as { toDate(): Date }).toDate()
          : undefined,
        createdAt: (data.createdAt as { toDate(): Date }).toDate(),
        lastSignInAt: (data.lastSignInAt as { toDate(): Date }).toDate(),
      })
    );
  } catch (error) {
    userServiceLogger.error("Firebase error in getUserProfile", {
      error,
      userId,
    });
    return Err(mapFirebaseError(error));
  }
}

/**
 * Parameters for updating a user profile
 */
export interface UpdateUserProfileParams {
  userId: string;
  updates: Partial<Pick<UserProfile, "firstName" | "lastName" | "avatarUrl">>;
}

/**
 * Update user profile data.
 * Writes are awaited to ensure acknowledgment.
 */
export async function updateUserProfile(
  params: UpdateUserProfileParams
): Promise<Result<void, AppError>> {
  const { userId, updates } = params;

  userServiceLogger.debug("Updating user profile", {
    userId,
    updates: Object.keys(updates),
  });

  const client = getFirestoreClient();
  const userPath = ["users", userId];
  const firestoreUpdates: Record<string, unknown> = {
    lastSignInAt: client.serverTimestamp(),
  };

  if (updates.firstName !== undefined)
    firestoreUpdates.firstName = updates.firstName;
  if (updates.lastName !== undefined)
    firestoreUpdates.lastName = updates.lastName;
  if (updates.avatarUrl !== undefined)
    firestoreUpdates.avatarUrl = updates.avatarUrl;

  await client.setDocument(userPath, firestoreUpdates, { merge: true });
  userServiceLogger.info("User profile updated", { userId });
  return Ok(undefined);
}

/**
 * Initialize settings documents for a user.
 * Writes are awaited to ensure all categories are created.
 *
 * @param userId - User ID
 * @returns Result indicating success or error
 */
export async function initializeUserSettings(
  userId: string
): Promise<Result<void, AppError>> {
  userServiceLogger.debug("Initializing settings documents for new user", {
    userId,
  });

  const client = getFirestoreClient();
  const categoriesToInitialize = Object.keys(
    nullSeedByCategory
  ) as SettingsCategory[];
  for (const category of categoriesToInitialize) {
    const settingsPath = ["users", userId, "settings", category];
    await client.setDocument(
      settingsPath,
      {
        ...nullSeedByCategory[category],
        createdAt: client.serverTimestamp(),
        updatedAt: client.serverTimestamp(),
      } as Record<string, unknown>,
      { merge: true }
    );
  }

  userServiceLogger.info("Settings documents initialized", { userId });
  return Ok(undefined);
}

/**
 * Record that a user has accepted the privacy notice.
 * Writes are awaited to ensure acknowledgment.
 *
 * @param userId - User ID
 * @returns Result indicating success or error
 */
export async function recordPrivacyAcceptance(
  userId: string
): Promise<Result<void, AppError>> {
  userServiceLogger.debug("Recording privacy acceptance", { userId });

  const client = getFirestoreClient();
  const userPath = ["users", userId];
  await client.setDocument(
    userPath,
    {
      privacyAcceptedAt: client.serverTimestamp(),
    },
    { merge: true }
  );

  userServiceLogger.info("Privacy acceptance recorded", { userId });
  return Ok(undefined);
}

/**
 * Known subcollection names under users/{userId}.
 * Each subcollection's documents must be deleted individually
 * before the parent user document can be fully removed.
 */
const USER_SUBCOLLECTIONS = [
  "settings",
  "scenarios",
  "intake",
  "onboarding",
] as const;

/**
 * Delete all Firestore data for a user, flush pending writes,
 * then delete the Firebase Auth account.
 *
 * Requires a fresh auth session — Firebase may throw
 * `auth/requires-recent-login` if the session is stale.
 */
export async function deleteUserAccount(
  userId: string,
  firebaseUser: { delete: () => Promise<void> }
): Promise<Result<void, AppError>> {
  userServiceLogger.info("Deleting user account", { userId });
  const client = getFirestoreClient();

  try {
    // 1. Delete all subcollection documents
    for (const sub of USER_SUBCOLLECTIONS) {
      const collPath = ["users", userId, sub];
      const result = await client.queryCollection(collPath);
      for (const doc of result.docs) {
        await client.deleteDocument([...collPath, doc.id]);
      }
    }

    // 2. Delete the user profile document
    await client.deleteDocument(["users", userId]);

    // 3. Delete the Firebase Auth account (invalidates the token)
    await firebaseUser.delete();

    userServiceLogger.info("User account deleted", { userId });
    return Ok(undefined);
  } catch (error) {
    userServiceLogger.error("Failed to delete user account", {
      error,
      userId,
    });
    return Err(
      createUnexpectedError({
        error,
        context: `Deleting user account userId=${userId}`,
      })
    );
  }
}
